// Kebab (⋯) action menu. Ports the pbx-portal ActionMenu component.
//
// Props:
//   items: [{ label, onClick, disabled?, danger?, divider? }]
//   label: alternate trigger text (default: '⋯' glyph)
//   title: accessible label + tooltip for the trigger (default 'Actions').
//          Pass something specific per row (e.g. `Actions for ${name}`) so
//          screen readers announce which row's menu is opening.
//
// Items rendered top-to-bottom in the given order. `divider: true`
// items render as a horizontal rule. Danger items get red text.
//
// Closes on: outside click, Escape, selecting an item, or the page scrolling.
//
// The menu is rendered into a PORTAL on document.body with fixed positioning, not
// absolutely inside the row. It used to be absolute, and that quietly breaks the
// moment a table sits in a scroll container: `overflow-x: auto` computes overflow-y
// to `auto` as well, so the container clips the dropdown and the menu opens
// invisibly behind the card. That happened to the Lines table the day it got an
// `overflow-x-auto` wrapper — the menu was there, focusable and keyboard-operable,
// just not on screen.
//
// A portal means no ancestor's overflow, z-index or transform can clip it, so this
// cannot regress the next time a table needs to scroll sideways. The cost is that
// fixed coordinates go stale when the page scrolls, so the menu closes on scroll
// rather than floating away from its row.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export default function ActionMenu({ items, label = null, title = 'Actions', className = '' }) {
  const [open, setOpen] = useState(false);
  // Viewport coordinates for the portalled menu, measured from the trigger when it
  // opens. `right` rather than `left` so the menu stays right-aligned to the kebab
  // without needing to know its own width first.
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        // ~est. menu height incl. dividers; flip when it wouldn't fit below so the
        // last rows of long tables stay usable.
        const dropUp = window.innerHeight - rect.bottom < 240;
        setPos({
          right: Math.max(8, window.innerWidth - rect.right),
          ...(dropUp
            ? { bottom: window.innerHeight - rect.top + 4 }
            : { top: rect.bottom + 4 }),
        });
      } else {
        setPos({ right: 8, top: 8 });
      }
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return undefined;
    // The menu lives outside `ref` now, so an outside-click check that only looked
    // at `ref` would close it on mousedown over its own items — and since mousedown
    // precedes click, the item would unmount before its onClick ever ran. Every
    // action would silently do nothing. Both nodes have to count as "inside".
    function onDown(e) {
      const inTrigger = ref.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    // Capture, so an inner scroll container counts too — fixed coordinates go stale
    // the moment anything scrolls, and a menu hovering beside the wrong row is worse
    // than one that closed.
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const visibleItems = (items || []).filter((it) => it && (it.divider || (it.label && (it.onClick || it.href))));
  if (visibleItems.length === 0) return null;

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={title}
        title={title}
      >
        {label ?? (
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
            <path d="M10 3.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM10 8.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM10 13.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
          </svg>
        )}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', ...pos }}
          // z-50 puts it above the app shell's banners and any card; being in a
          // portal means it is not competing with an ancestor's stacking context
          // either way.
          className="z-50 min-w-[10rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {visibleItems.map((item, i) => {
            if (item.divider) {
              return <div key={`div-${i}`} className="my-1 h-px bg-gray-100" />;
            }
            const cls = item.danger
              ? 'text-red-600 hover:bg-red-50 disabled:text-red-300'
              : 'text-gray-700 hover:bg-gray-50 disabled:text-gray-300';
            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (!item.disabled) item.onClick?.();
                }}
                title={item.title || undefined}
                className={`block w-full px-3 py-1.5 text-left text-sm ${cls} disabled:cursor-not-allowed`}
              >
                {item.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
